import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from 'src/common/utils/api';
import { API_URL } from 'src/common/utils/api/fetchWrapper';
import { AuthMethod } from 'src/auth/settings/types';

export type TenantConfig = {
  id: string;
  companyId: string;
  authMethod: AuthMethod;
  botName: string | null;
  botImageS3Key: string | null;
};

export const useTenantConfig = (companyId: string) => {
  const { data, isLoading, isError, error, refetch } = useQuery<TenantConfig>({
    queryKey: ['admin', 'companies', companyId, 'config'],
    enabled: !!companyId,
  });

  return {
    config: data,
    isLoading,
    isError,
    error,
    refetch,
  };
};

type UpdateTenantConfigInput = {
  authMethod: AuthMethod;
};

export const useUpdateTenantConfig = (companyId: string) => {
  const queryClient = useQueryClient();

  const {
    mutateAsync,
    isPending: isLoading,
    error,
  } = useMutation<{ data: TenantConfig }, Error, UpdateTenantConfigInput>({
    mutationFn: (input: UpdateTenantConfigInput) =>
      api.put<TenantConfig>(`admin/companies/${companyId}/config`, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'companies', companyId, 'config'] });
    },
  });

  const updateConfig = async (input: UpdateTenantConfigInput) => {
    return mutateAsync(input);
  };

  return {
    updateConfig,
    isLoading,
    error,
  };
};

export const useSetBotName = (companyId: string) => {
  const queryClient = useQueryClient();

  const {
    mutateAsync,
    isPending: isLoading,
    error,
  } = useMutation<{ data: TenantConfig }, Error, string>({
    mutationFn: (botName: string) =>
      api.put<TenantConfig>(`admin/companies/${companyId}/config/bot-name`, { botName }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'companies', companyId, 'config'] });
    },
  });

  return {
    setBotName: mutateAsync,
    isLoading,
    error,
  };
};

export const useClearBotName = (companyId: string) => {
  const queryClient = useQueryClient();

  const {
    mutateAsync,
    isPending: isLoading,
    error,
  } = useMutation<{ data: TenantConfig }, Error, void>({
    mutationFn: () => api.delete<TenantConfig>(`admin/companies/${companyId}/config/bot-name`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'companies', companyId, 'config'] });
    },
  });

  return {
    clearBotName: mutateAsync,
    isLoading,
    error,
  };
};

type LocationResponse = {
  location: string;
};

type UploadUrlResponse = {
  uploadUrl: string;
  location: string;
};

export const useUploadBotImage = (companyId: string) => {
  const queryClient = useQueryClient();

  const {
    mutateAsync,
    isPending: isLoading,
    error,
  } = useMutation<LocationResponse, Error, File>({
    mutationFn: async (file: File) => {
      const uploadUrlResponse = await fetch(
        `${API_URL}/admin/companies/${companyId}/config/bot-image/upload-url`,
        {
          method: 'POST',
          credentials: 'include',
        },
      );

      if (!uploadUrlResponse.ok) {
        throw new Error(`Failed to get upload URL: ${uploadUrlResponse.statusText}`);
      }

      const { uploadUrl } = (await uploadUrlResponse.json()) as UploadUrlResponse;

      const s3Response = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type,
        },
      });

      if (!s3Response.ok) {
        throw new Error(`S3 upload failed: ${s3Response.statusText}`);
      }

      const confirmResponse = await fetch(
        `${API_URL}/admin/companies/${companyId}/config/bot-image/confirm`,
        {
          method: 'POST',
          credentials: 'include',
        },
      );

      if (!confirmResponse.ok) {
        throw new Error(`Confirm failed: ${confirmResponse.statusText}`);
      }

      return confirmResponse.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'companies', companyId, 'config'] });
    },
  });

  return {
    uploadBotImage: mutateAsync,
    isLoading,
    error,
  };
};

export const useDeleteBotImage = (companyId: string) => {
  const queryClient = useQueryClient();

  const {
    mutateAsync,
    isPending: isLoading,
    error,
  } = useMutation<void, Error, void>({
    mutationFn: async () => {
      const response = await fetch(`${API_URL}/admin/companies/${companyId}/config/bot-image`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`Delete failed: ${response.statusText}`);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'companies', companyId, 'config'] });
      queryClient.invalidateQueries({
        queryKey: ['admin', 'companies', companyId, 'config', 'bot-image', 'download-url'],
      });
    },
  });

  return {
    deleteBotImage: mutateAsync,
    isLoading,
    error,
  };
};

type BotImageDownloadUrlResponse = {
  downloadUrl: string | null;
};

export const useBotImageDownloadUrl = (companyId: string, enabled: boolean) => {
  const { data, isLoading, refetch } = useQuery<BotImageDownloadUrlResponse>({
    queryKey: ['admin', 'companies', companyId, 'config', 'bot-image', 'download-url'],
    enabled: enabled && !!companyId,
  });

  return {
    downloadUrl: data?.downloadUrl ?? null,
    isLoading,
    refetch,
  };
};
